import { AnimatePresence, animate, motion } from "framer-motion";
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  Scale,
  Shield,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import Image from "next/image";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import AppContext from "src/components/AppContext";
import moment from "moment-timezone";
import {
  ForensicInvestigation,
  FORENSIC_TOTAL_MAX,
  getForensicScoreColor,
  getForensicScoreDimensions,
  getVerdictBadgeClass,
  parseForensicScore,
  toForensicGaugeScore,
} from "src/components/dashboard/forensicInvestigation.types";
import { fetchWrapper } from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";

interface ForensicInvestigationModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  selectedCompany: string;
  /** Reduced height when nested inside another modal (e.g. AnalyzeStockModal) */
  compact?: boolean;
}

type ForensicSectionDef = {
  id: string;
  label: string;
  icon: React.ElementType;
};

function ManagementScoreGauge({
  totalScore,
  size = 120,
}: {
  totalScore: number;
  size?: number;
}) {
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const gaugeScore = toForensicGaugeScore(totalScore);
  const gradientId = useId().replace(/:/g, "");
  const clamped = Math.max(0, Math.min(10, gaugeScore));
  const [displayScore, setDisplayScore] = useState(0);
  const [prevClamped, setPrevClamped] = useState(clamped);

  if (clamped !== prevClamped) {
    setDisplayScore(0);
    setPrevClamped(clamped);
  }

  const gaugeHeight = Math.round(size * 0.48);
  const cx = 50;
  const cy = 46;
  const radius = 38;
  const needleLen = radius * 0.72;
  const startAngle = Math.PI;
  const endAngle = Math.PI * (1 - clamped / 10);
  const startX = cx + needleLen * Math.cos(startAngle);
  const startY = cy - needleLen * Math.sin(startAngle);
  const endX = cx + needleLen * Math.cos(endAngle);
  const endY = cy - needleLen * Math.sin(endAngle);
  const color = getForensicScoreColor(totalScore, false, FORENSIC_TOTAL_MAX);
  const track = darkMode ? "#334155" : "#e2e8f0";
  const labelColor = darkMode ? "#94a3b8" : "#64748b";
  const arcPath = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`;

  useEffect(() => {
    const controls = animate(0, clamped, {
      duration: 1.15,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (value) => setDisplayScore(value),
    });
    return () => controls.stop();
  }, [clamped]);

  return (
    <div
      className="relative flex shrink-0 flex-col items-center"
      style={{ width: size, minWidth: size }}
      aria-label={`Management score ${displayScore.toFixed(1)} out of 10`}
    >
      <motion.span
        key={clamped}
        className="mb-0.5 text-lg font-bold tabular-nums text-gray-900 dark:text-white"
        initial={{ opacity: 0.4 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {displayScore.toFixed(1)}
      </motion.span>
      <svg
        viewBox="0 0 100 52"
        width={size}
        height={gaugeHeight}
        className="overflow-visible"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ef4444" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#00A85F" />
          </linearGradient>
        </defs>
        <path
          d={arcPath}
          fill="none"
          stroke={track}
          strokeWidth={6}
          strokeLinecap="round"
        />
        <path
          d={arcPath}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={6}
          strokeLinecap="round"
          opacity={0.9}
        />
        <motion.line
          key={clamped}
          x1={cx}
          y1={cy}
          initial={{ x2: startX, y2: startY }}
          animate={{ x2: endX, y2: endY }}
          transition={{ duration: 1.15, ease: [0.22, 1, 0.36, 1] }}
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={3.5} fill={color} />
      </svg>
      <div
        className="mt-0.5 flex w-full items-center justify-between px-0.5 text-[10px] font-semibold leading-none"
        style={{ color: labelColor }}
      >
        <span className="shrink-0">Poor</span>
        <span className="shrink-0 text-center text-[9px] font-medium normal-case tracking-normal">
          {totalScore}/{FORENSIC_TOTAL_MAX}
        </span>
        <span className="shrink-0">Excellent</span>
      </div>
    </div>
  );
}

function ExpandableText({
  text,
  previewChars = 180,
  className = "text-sm leading-relaxed text-gray-600 dark:text-gray-300",
}: {
  text: string;
  previewChars?: number;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsExpand = text.length > previewChars;

  return (
    <div>
      <p className={className}>
        {needsExpand && !expanded
          ? `${text.slice(0, previewChars).trim()}…`
          : text}
      </p>
      {needsExpand ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400 cursor-pointer"
        >
          {expanded ? "Show less" : "Read more"}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      ) : null}
    </div>
  );
}

function DimensionAccordion({
  dimensions,
  openKey,
  onOpenKey,
}: {
  dimensions: ReturnType<typeof getForensicScoreDimensions>;
  openKey: string | null;
  onOpenKey: (key: string) => void;
}) {
  const withDetails = dimensions.filter((d) => d.justification?.trim());

  if (!withDetails.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No dimension details available.
      </p>
    );
  }

  return (
    <div className="divide-y divide-[#d0d4e4]/70 dark:divide-[#394564]/80">
      {withDetails.map((dimension) => {
        const isOpen = openKey === dimension.key;
        const color = getForensicScoreColor(
          dimension.score,
          dimension.isPenalty,
          dimension.isPenalty ? 10 : dimension.max,
        );

        return (
          <div key={dimension.key} id={`dimension-${dimension.key}`}>
            <button
              type="button"
              onClick={() => onOpenKey(isOpen ? "" : dimension.key)}
              className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors ${
                isOpen
                  ? "bg-gray-50 dark:bg-gray-800/40"
                  : "hover:bg-gray-50/80 dark:hover:bg-gray-800/25"
              }`}
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums"
                style={{
                  backgroundColor: `${color}22`,
                  color,
                }}
              >
                {dimension.isPenalty && dimension.score > 0 ? "+" : ""}
                {dimension.score}
              </span>
              <span className="min-w-0 flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">
                {dimension.label}
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className="overflow-hidden"
                >
                  <p className="pb-3 pl-10 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                    {dimension.justification}
                  </p>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function RiskVectorList({ items }: { items: string[] }) {
  return (
    <ol className="relative space-y-0">
      {items.map((item, index) => (
        <RiskVectorItem
          key={index}
          text={item}
          index={index}
          isLast={index === items.length - 1}
        />
      ))}
    </ol>
  );
}

function RiskVectorItem({
  text,
  index,
  isLast,
}: {
  text: string;
  index: number;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const needsExpand = text.length > 180;
  const preview = needsExpand ? `${text.slice(0, 180).trim()}…` : text;

  return (
    <li className="relative flex gap-3 pb-4">
      {!isLast ? (
        <span
          className="absolute left-[13px] top-7 bottom-0 w-px bg-gray-200/80 dark:bg-gray-700/50"
          aria-hidden
        />
      ) : null}
      <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        {index + 1}
      </span>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
          {expanded ? text : preview}
        </p>
        {needsExpand ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400 cursor-pointer"
          >
            {expanded ? "Show less" : "Read more"}
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        ) : null}
      </div>
    </li>
  );
}

function GovernanceTabs({
  items,
}: {
  items: {
    key: string;
    icon: React.ElementType;
    title: string;
    content: string;
  }[];
}) {
  const available = items.filter((item) => item.content?.trim());
  const fallbackKey = available[0]?.key ?? "";
  const [activeKey, setActiveKey] = useState(fallbackKey);

  if (
    !available.some((item) => item.key === activeKey) &&
    activeKey !== fallbackKey
  ) {
    setActiveKey(fallbackKey);
  }

  if (!available.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No governance details available.
      </p>
    );
  }

  const active =
    available.find((item) => item.key === activeKey) ?? available[0];

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {available.map((item) => {
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setActiveKey(item.key)}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm dark:bg-blue-500"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
            >
              <item.icon className="h-3 w-3 shrink-0" />
              <span className="truncate">{item.title}</span>
            </button>
          );
        })}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active.key}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
        >
          <ExpandableText
            text={active.content}
            previewChars={320}
            className="text-sm leading-relaxed text-gray-600 dark:text-gray-300"
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function SidebarSummary({
  record,
  totalScore,
  dimensions,
  activeDimension,
  onDimensionClick,
}: {
  record: ForensicInvestigation;
  totalScore: number;
  dimensions: ReturnType<typeof getForensicScoreDimensions>;
  activeDimension: string | null;
  onDimensionClick: (key: string) => void;
}) {
  return (
    <div className="mt-3 hidden flex-col gap-3 border-t border-[#d0d4e4] pt-3 dark:border-[#394564] lg:flex">
      <div className="flex flex-col items-center px-1">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Management Score
        </p>
        <ManagementScoreGauge totalScore={totalScore} size={108} />
      </div>

      <span
        className={`inline-flex justify-center rounded-full px-2.5 py-1 text-[10px] font-semibold leading-snug ${getVerdictBadgeClass(record.verdict_category)}`}
      >
        {record.verdict_category}
      </span>

      <div className="space-y-1">
        <p className="px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Score Breakdown
        </p>
        {dimensions.map((dimension) => {
          const color = getForensicScoreColor(
            dimension.score,
            dimension.isPenalty,
            dimension.isPenalty ? 10 : dimension.max,
          );
          const widthPercent = dimension.isPenalty
            ? Math.min(100, (Math.abs(dimension.score) / 10) * 100)
            : dimension.max > 0
              ? Math.min(100, (dimension.score / dimension.max) * 100)
              : 0;
          const isActive = activeDimension === dimension.key;

          return (
            <button
              key={dimension.key}
              type="button"
              onClick={() => onDimensionClick(dimension.key)}
              className={`w-full cursor-pointer rounded-md px-1 py-1.5 text-left transition-colors ${
                isActive
                  ? "bg-blue-50 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:ring-blue-700/50"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
              }`}
            >
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
                <span className="truncate text-gray-600 dark:text-gray-300">
                  {dimension.label}
                </span>
                <span
                  className="shrink-0 font-semibold tabular-nums"
                  style={{ color }}
                >
                  {dimension.isPenalty && dimension.score > 0 ? "+" : ""}
                  {dimension.score}
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${widthPercent}%`, backgroundColor: color }}
                />
              </div>
            </button>
          );
        })}
        <p className="px-1 pt-1 text-[10px] text-gray-400 dark:text-gray-500">
          Tap a dimension to read its analysis
        </p>
      </div>

      <p className="px-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {record.sector}
        </span>
        {record.industry ? (
          <span className="mt-0.5 block text-[10px]">{record.industry}</span>
        ) : null}
      </p>
    </div>
  );
}

function ContentSection({
  id,
  sectionRef,
  icon: Icon,
  title,
  subtitle,
  isActive,
  children,
}: {
  id: string;
  sectionRef: (el: HTMLElement | null) => void;
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  isActive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section id={id} ref={sectionRef} className="scroll-mt-6 pb-10 last:pb-4">
      <div className="mb-3 flex items-start gap-3">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
            isActive
              ? "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400"
              : "bg-gray-100/80 text-gray-500 dark:bg-gray-800/60 dark:text-gray-400"
          }`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function buildForensicSections(
  record: ForensicInvestigation,
  riskCount: number,
): ForensicSectionDef[] {
  const sections: ForensicSectionDef[] = [
    { id: "overview", label: "Overview", icon: Sparkles },
    { id: "details", label: "Dimension Details", icon: Target },
    { id: "risks", label: "Risk Vectors", icon: AlertTriangle },
    { id: "governance", label: "Governance & Leadership", icon: Users },
  ];

  if (record.peer_context?.trim()) {
    sections.push({ id: "peers", label: "Peer Context", icon: Scale });
  }

  if (riskCount === 0) {
    return sections.filter((section) => section.id !== "risks");
  }

  return sections;
}

const ForensicInvestigationContent = ({
  selectedCompany,
  onRecordLoaded,
}: {
  selectedCompany: string;
  onRecordLoaded?: (updatedAt: string) => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const navRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const isJumpingRef = useRef(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [openDimension, setOpenDimension] = useState<string | null>(null);
  const [record, setRecord] = useState<ForensicInvestigation | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    const fetchRecord = async () => {
      if (!selectedCompany) {
        setRecord(null);
        return;
      }

      setLoading(true);
      try {
        const user = getLocalStorageUser();
        const response = await fetchWrapper.get(
          `/api/dashboard/forensic_investigations?symbol=${encodeURIComponent(selectedCompany)}`,
          undefined,
          user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        );

        if (active) {
          const rec = response?.success ? (response.data ?? null) : null;
          setRecord(rec);
          if (rec?.updated_at) {
            onRecordLoaded?.(rec.updated_at);
          }
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setRecord(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void fetchRecord();
    return () => {
      active = false;
    };
  }, [selectedCompany, onRecordLoaded]);

  const dimensions = useMemo(
    () => (record ? getForensicScoreDimensions(record) : []),
    [record],
  );

  const totalScore = parseForensicScore(record?.total_management_score);
  const riskVectors = useMemo(
    () =>
      record
        ? [
            record.risk_vector_1,
            record.risk_vector_2,
            record.risk_vector_3,
          ].filter(Boolean)
        : [],
    [record],
  );

  const sections = useMemo(
    () => (record ? buildForensicSections(record, riskVectors.length) : []),
    [record, riskVectors.length],
  );

  const sectionById = useMemo(
    () => Object.fromEntries(sections.map((s) => [s.id, s])),
    [sections],
  );

  useEffect(() => {
    setActiveSection("overview");
    const firstWithDetails = dimensions.find((d) => d.justification?.trim());
    setOpenDimension(firstWithDetails?.key ?? null);
  }, [selectedCompany, dimensions]);

  const scrollToSection = useCallback((id: string) => {
    const container = scrollRef.current;
    const el = sectionRefs.current[id];
    if (!container || !el) return;

    isJumpingRef.current = true;
    setActiveSection(id);

    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;

    container.scrollTo({ top: Math.max(0, top - 12), behavior: "smooth" });
    window.setTimeout(() => {
      isJumpingRef.current = false;
    }, 900);
  }, []);

  const scrollToDimension = useCallback(
    (key: string) => {
      setOpenDimension(key);
      scrollToSection("details");
      window.setTimeout(() => {
        document
          .getElementById(`dimension-${key}`)
          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 350);
    },
    [scrollToSection],
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !record || sections.length === 0) return;

    const updateActiveSection = () => {
      if (isJumpingRef.current) return;

      const containerRect = container.getBoundingClientRect();
      const scrollRemaining =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (scrollRemaining < 24) {
        const lastId = sections[sections.length - 1]?.id ?? "overview";
        setActiveSection((prev) => (prev === lastId ? prev : lastId));
        return;
      }

      const markerY = containerRect.top + 112;
      let currentId = sections[0]?.id ?? "overview";

      for (const { id } of sections) {
        const el = sectionRefs.current[id];
        if (!el) continue;

        const { top, bottom } = el.getBoundingClientRect();
        if (top <= markerY && bottom > markerY) {
          currentId = id;
          break;
        }
        if (top <= markerY) {
          currentId = id;
        }
      }

      setActiveSection((prev) => (prev === currentId ? prev : currentId));
    };

    container.addEventListener("scroll", updateActiveSection, {
      passive: true,
    });
    updateActiveSection();

    return () => container.removeEventListener("scroll", updateActiveSection);
  }, [sections, record]);

  useEffect(() => {
    if (!activeSection) return;
    navRefs.current[activeSection]?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });
  }, [activeSection]);

  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center px-6 text-center">
        <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#00A85F] border-t-transparent" />
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
          Loading forensic investigation...
        </p>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center px-6 text-center">
        <ShieldAlert className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
        <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">
          Forensic investigation not available yet
        </p>
        <p className="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
          Management quality analysis for <strong>{selectedCompany}</strong> is
          coming soon.
        </p>
      </div>
    );
  }

  const governanceItems = [
    {
      key: "promoter",
      icon: Building2,
      title: "Promoter Architecture",
      content: record.promoter_architecture,
    },
    {
      key: "stints",
      icon: TrendingUp,
      title: "Prior Stints & Value",
      content: record.prior_stints_value,
    },
    {
      key: "capital",
      icon: Shield,
      title: "Capital Allocation",
      content: record.capital_allocation_governance,
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f8fafc] dark:bg-[#121419] lg:flex-row">
      <aside className="custom-scroll shrink-0 overflow-x-auto border-[#d0d4e4] bg-[#f8fafc] dark:border-[#394564] dark:bg-gray-900/90 lg:w-44 lg:overflow-y-auto lg:border-r xl:w-48">
        <div className="p-2 lg:flex lg:h-full lg:flex-col lg:px-3 lg:py-4">
          <nav className="flex min-w-max gap-0.5 lg:min-w-0 lg:flex-col">
            {sections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  ref={(el) => {
                    navRefs.current[section.id] = el;
                  }}
                  type="button"
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => scrollToSection(section.id)}
                  className={`relative flex w-full cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-xs font-medium transition-all lg:whitespace-normal ${
                    isActive
                      ? "bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700/50"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/50 dark:hover:text-gray-200"
                  }`}
                >
                  {isActive ? (
                    <span className="absolute bottom-2 left-0 top-2 w-1 rounded-full bg-blue-600 dark:bg-blue-400" />
                  ) : null}
                  <section.icon
                    className={`h-4 w-4 shrink-0 ${isActive ? "text-blue-500" : "opacity-50"}`}
                  />
                  <span className="flex-1 leading-snug">{section.label}</span>
                </button>
              );
            })}
          </nav>

          <SidebarSummary
            record={record}
            totalScore={totalScore}
            dimensions={dimensions}
            activeDimension={activeSection === "details" ? openDimension : null}
            onDimensionClick={scrollToDimension}
          />
        </div>
      </aside>

      <main
        ref={scrollRef}
        className="custom-scroll min-h-0 flex-1 overflow-y-auto bg-[#f8fafc] dark:bg-gray-900/90"
      >
        <div className="space-y-2 px-4 py-5 pb-10 sm:px-6 lg:px-8">
          <ContentSection
            id="overview"
            sectionRef={setSectionRef("overview")}
            icon={sectionById.overview.icon}
            title={sectionById.overview.label}
            subtitle={record.company_name}
            isActive={activeSection === "overview"}
          >
            <div className="space-y-4">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getVerdictBadgeClass(record.verdict_category)}`}
              >
                {record.verdict_category}
              </span>
              {record.verdict_summary ? (
                <ExpandableText
                  text={record.verdict_summary}
                  previewChars={240}
                  className="text-sm leading-relaxed text-gray-700 dark:text-gray-200"
                />
              ) : null}
              <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="rounded-md bg-gray-100 px-2 py-1 dark:bg-gray-800">
                  {record.sector}
                </span>
                {record.industry ? (
                  <span className="rounded-md bg-gray-100 px-2 py-1 dark:bg-gray-800">
                    {record.industry}
                  </span>
                ) : null}
              </div>
            </div>
          </ContentSection>

          <ContentSection
            id="details"
            sectionRef={setSectionRef("details")}
            icon={sectionById.details.icon}
            title={sectionById.details.label}
            subtitle="Tap each dimension to expand the full analysis"
            isActive={activeSection === "details"}
          >
            <DimensionAccordion
              dimensions={dimensions}
              openKey={openDimension}
              onOpenKey={(key) => setOpenDimension(key || null)}
            />
          </ContentSection>

          {sectionById.risks ? (
            <ContentSection
              id="risks"
              sectionRef={setSectionRef("risks")}
              icon={sectionById.risks.icon}
              title={sectionById.risks.label}
              subtitle={`${riskVectors.length} identified risk${riskVectors.length === 1 ? "" : "s"}`}
              isActive={activeSection === "risks"}
            >
              <RiskVectorList items={riskVectors} />
            </ContentSection>
          ) : null}

          <ContentSection
            id="governance"
            sectionRef={setSectionRef("governance")}
            icon={sectionById.governance.icon}
            title={sectionById.governance.label}
            subtitle="Promoter track record and capital discipline"
            isActive={activeSection === "governance"}
          >
            <GovernanceTabs items={governanceItems} />
          </ContentSection>

          {sectionById.peers ? (
            <ContentSection
              id="peers"
              sectionRef={setSectionRef("peers")}
              icon={sectionById.peers.icon}
              title={sectionById.peers.label}
              subtitle="How management compares to industry peers"
              isActive={activeSection === "peers"}
            >
              <ExpandableText
                text={record.peer_context}
                previewChars={400}
                className="text-sm leading-relaxed text-gray-600 dark:text-gray-300"
              />
            </ContentSection>
          ) : null}
        </div>
      </main>
    </div>
  );
};

const ForensicInvestigationModal = ({
  show,
  setShow,
  selectedCompany,
  compact = false,
}: ForensicInvestigationModalProps) => {
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  if (!show) return null;

  const heightClass = compact
    ? "h-[68vh] max-h-[68vh]"
    : "h-[88vh] max-h-[88vh]";
  /** Stay inside AnalyzeStockModal (max-w-[85vw]) on ≤1600px screens */
  const widthClass = compact
    ? "max-w-[min(78vw,72rem)]"
    : "max-w-[min(96vw,88rem)]";

  return (
    <div
      className="fixed inset-0 z-[9999] !mt-0 flex items-center justify-center px-[2vw] py-4 backdrop-blur-md"
      onClick={() => setShow(false)}
    >
      <div
        className={`z-10 flex ${heightClass} w-full ${widthClass} flex-col overflow-hidden rounded-xl border border-[#d0d4e4] bg-white shadow-2xl dark:border-[#394564] dark:bg-gray-800`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#d0d4e4] bg-white px-5 py-3 dark:border-[#394564] dark:bg-[#30324e]/40">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex-shrink-0 rounded-md border border-gray-300 bg-white p-1 shadow-sm dark:border-gray-600">
              <Image
                src={`https://firebasestorage.googleapis.com/v0/b/abillion-analytics.appspot.com/o/${selectedCompany}.jpg?alt=media`}
                alt={selectedCompany}
                width={40}
                height={40}
                unoptimized
                className="h-8 w-auto object-contain"
              />
            </span>
            <h2 className="truncate text-xl font-bold dark:text-white">
              {selectedCompany} | Forensic Investigation
            </h2>
          </div>
          <div className="flex items-start gap-2">
            <div className="flex flex-col items-end gap-1">
              {updatedAt && (
                <span className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 dark:bg-[#30324e]/80 dark:text-gray-300 border border-gray-200 dark:border-transparent">
                  Last Updated:{" "}
                  {moment
                    .tz(updatedAt, "Asia/Kolkata")
                    .format("DD-MM-YYYY HH:mm:ss")}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[11px] leading-[11px] font-medium text-gray-400 dark:text-gray-400">
                <AlertTriangle
                  className="block h-[11px] w-[11px] shrink-0"
                  strokeWidth={2}
                  aria-hidden="true"
                />
                Few information has been sourced using AI.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShow(false)}
              className="shrink-0 cursor-pointer rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <ForensicInvestigationContent
            selectedCompany={selectedCompany}
            onRecordLoaded={setUpdatedAt}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(ForensicInvestigationModal);
